import { Component, signal } from '@angular/core';
import { EzFormComponent } from "@template-ui/components/ez-form/ez-form.component";
import { AppBaseComponent } from "../../../app-base.component";
import { EZDropdown, InputConfig } from "@template-ui/types";
import { DocMeta } from "@template-ui/decorators";

@DocMeta({
  hide: true,
  showcaseTitle: "Dependent Dropdowns",
  showcaseDesc: `
  This sample demonstrates **dependent dropdowns** in a dynamic form using the \`EZDropdown\` system.

  - The **Report** dropdown options depend on selections made in the **Report Category** and **Report Type** dropdowns.
  - Selecting different options in **Report Category** changes the available options in **Report Type** and **Report**.
  - The **Report Type** dropdown is conditionally displayed only when **Report Category** is set to \`BIZ\`.
  - Filtering logic for the **Report** dropdown is handled in the \`filterOnParents\` function of its configuration.
  - Form change events are managed in the \`onFormChange\` method.

  ### EZDropdown System

  The \`EZDropdown\` system provides a flexible way to define dropdowns with dependencies, API endpoints, static items, and custom filtering logic.

  #### Key Classes & Interfaces

  - \`EZDropdown<T>\`: Main interface for dropdown configuration. Supports:
    - \`endpoint\`: API URL for fetching options.
    - \`staticItems\`: Local array of options.
    - \`getValue\`, \`getLabel\`: Functions to extract value/label from each item.
    - \`dependsOnMany\`: Array of parent dropdown configs for dependency.
    - \`filterOnParents\`: Function to filter options based on parent values.
    - \`cacheDropdown\`: Enables caching of dropdown options.

  - \`DropdownOptionsService\`: Service for retrieving dropdown options, applying filters, and managing cache.

  - \`InputSelectOption\`: Standardized option type for dropdowns.

  - \`commonDropdownConfigFactories\`: Registry of reusable dropdown config factories.

  #### Usage

  1. **Define dropdown configs** using \`EZDropdown\` and register them.
  2. **Configure dependencies** using \`dependsOnMany\` and \`filterOnParents\`.
  3. **Use in forms** by passing the config to the form input.

  See the code for practical examples of configuring and using dependent dropdowns.
  `
})
@Component({
  selector: 'app-dependent-dropdown-sample',
  imports: [EzFormComponent],
  templateUrl: './dependent-dropdown-sample.component.html',
  styleUrls: ['./dependent-dropdown-sample.component.scss']
})
export class DependentDropdownSampleComponent extends AppBaseComponent {

  // Controls visibility of the Report Type dropdown based on Report Category selection
  showTypeDropdown = signal(false);
  showInstitutionDropdown = signal(false);

  /**
   * Dropdown config for Report Category.
   * - Fetches categories from API.
   * - Used as a parent for dependent dropdowns.
   */
  reportCategoryDropdownConfig: EZDropdown = {
    name: "reportCategory",
    endpoint: this.apis.core.getReportCategories(),
    endpointVerbType: 'GET',
    getValue: i => i.value,
    getLabel: i => i.label,
    includeAllOption: true,
    allOptionLabel: 'All'
  };

  /**
   * Dropdown config for Report Type.
   * - Fetches types from API.
   * - Shown only when Report Category is 'BIZ'.
   * - Used as a parent for the Report dropdown.
   */
  reportTypeDropdownConfig: EZDropdown = {
    name: "reportType",
    endpoint: this.apis.core.getReportBusinessReportTypes(),
    endpointVerbType: 'GET',
    getValue: i => i.value,
    getLabel: i => i.label,
  };

  /**
   * Dropdown config for Report.
   * - Depends on both Report Category and Report Type.
   * - Uses filterOnParents to filter options based on parent selections.
   */
  reportsDropdownConfig: EZDropdown = {
    name: "reportId",
    endpoint: this.apis.core.getReport(),
    endpointVerbType: 'GET',
    getValue: i => i.reportId,
    getLabel: i => i.reportName,
    dependsOnMany: [this.reportCategoryDropdownConfig, this.reportTypeDropdownConfig],

    // Custom filter logic based on parent dropdown values
    filterOnParents: (parents, items) => {
      const cat = parents['get'](this.reportCategoryDropdownConfig);
      const typ = parents['get'](this.reportTypeDropdownConfig);

      const allValue = this.reportCategoryDropdownConfig.allOptionValue ?? '__ALL__';
      if (cat === allValue) {
        return items; // Show all options
      }
      if (cat === 'BIZ') {
        // When category is 'BIZ', filter by businessReportType
        const filtered = items.filter(i => i.reportType === 'BIZ' && i.businessReportType === typ);
        return filtered;
      }
      // Otherwise, filter by category name or type
      const filtered = items.filter(i => i.reportCategoryName === cat || i.reportType === cat);
      return filtered;
    }
  };

  /**
   * Form configuration for BitsDynamicFormComponent.
   * - Each input uses a dropdownConfig for options.
   * - The Report Type dropdown is conditionally shown.
   */
  reportsFormConfig: InputConfig[] = [
    { type: 'select', name: 'reportCategory', label: 'Report Category', row: 1, width: '2x', dropdownConfig: this.reportCategoryDropdownConfig },
    { type: 'select', name: 'reportType', label: 'Report Type', row: 2, width: '2x', dropdownConfig: this.reportTypeDropdownConfig, show: this.showTypeDropdown },
    { type: 'select', name: 'reportId', label: 'Report', row: 3, width: '2x', dropdownConfig: this.reportsDropdownConfig },
  ];

  /**
   * Handles form changes.
   * - Shows/hides the Report Type dropdown based on Report Category selection.
   */
  onFormChange(event: any) {
    if (event.name == 'reportCategory') {
      this.showTypeDropdown.set(event.value === 'BIZ');
    } else if (event.name === 'reportId') {
      this.showInstitutionDropdown.set(event.value === 101); // Example condition to show Institution dropdown
    }
  }


}
